import {
  Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Link, Stack, TextField, Typography,
} from '@mui/material';
import { ContentCopyOutlined, EmailOutlined, ExpandMore, OpenInNewOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { FunctionComponent, useContext, useState } from 'react';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import { AWS_INSTRUCTIONS_NOTE_MAX, ICloudConnectionStatus } from '../shared-services/cloud-emissions/cloud-emissions.model';
import { SetupInstructionsDialog } from '../shared-components/components/setup-instructions-dialog';
import { CodeBlock, CopyRow } from './cloud-connect-blocks';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };

/**
 * Connecting Google Cloud: the customer exports Carbon Footprint to a BigQuery table, gives one
 * service account of ours BigQuery Data Viewer on it, and labels it with the code issued here. No
 * Google Cloud secret is ever entered.
 *
 * Built like the AWS panel, because the person who can do this is usually their Google Cloud admin:
 * email the instructions (or copy them into a ticket), let the admin run two Cloud Shell commands
 * or use the console, then enter the table name they send back. Connect checks access, the label
 * and the table before saving, so a half-made setup never looks connected. The label is what stops
 * another organisation connecting this table: our service account can read every table granted to
 * it, so the code must be on the table.
 */
export const GcpConnectPanel: FunctionComponent<{
  status: ICloudConnectionStatus,
  isAdmin: boolean,
  onUpdated: (status: ICloudConnectionStatus) => void,
}> = ({ status, isAdmin, onUpdated }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const gcp = status.gcp ?? null;
  const [table, setTable] = useState(gcp?.table ?? '');
  const [isStarting, setIsStarting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ email: string, name: string }>({ email: '', name: '' });

  const code = gcp?.code ?? null;
  const serviceAccount = gcp?.serviceAccount ?? null;
  const lastSent = gcp?.lastInstructionsSent ?? null;
  const commandsText = gcp?.commands ? `${gcp.commands.grant}\n${gcp.commands.label}` : '';

  const errorSnackbar = (err: any, fallback: string) => userNotificationsContext.openSnackbar({
    open: true,
    message: err?.response?.data?.message || fallback,
    backgroundColor: 'text.primary',
  });

  const copy = (text: string, successMessage: string) => {
    navigator.clipboard.writeText(text)
      .then(() => userNotificationsContext.openSnackbar({ open: true, message: successMessage, backgroundColor: 'text.primary' }))
      .catch(() => userNotificationsContext.openSnackbar({ open: true, message: 'Could not copy; select the text by hand', backgroundColor: 'text.primary' }));
  };

  const startSetup = () => {
    setIsStarting(true);
    cloudEmissionsService.setupGcp()
      .then(onUpdated)
      .catch(err => errorSnackbar(err, 'The Google Cloud setup could not be started. Please try again.'))
      .finally(() => setIsStarting(false));
  };

  const openDialog = (resend: boolean) => {
    setPrefill(resend && lastSent ? { email: lastSent.email, name: lastSent.name ?? '' } : { email: '', name: '' });
    setDialogOpen(true);
  };

  const connect = () => {
    setIsConnecting(true);
    setConnectError(null);
    cloudEmissionsService.connectGcp(table)
      .then(updated => {
        const count = updated.connectResult?.projectCount;
        userNotificationsContext.openSnackbar({
          open: true,
          message: !count
            ? 'Google Cloud connected'
            : `Google Cloud connected. The table has carbon figures for ${count} ${count === 1 ? 'project' : 'projects'}.`,
          backgroundColor: 'primary.dark',
        });
        onUpdated(updated);
      })
      .catch(err => setConnectError(err?.response?.data?.message || 'The table could not be saved.'))
      .finally(() => setIsConnecting(false));
  };

  if (!isAdmin) {
    return (
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        Ask an admin in your organisation to connect Google Cloud.
      </Typography>
    );
  }

  if (!code) {
    return (
      <Card variant='outlined' sx={cardSx}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
            <Stack spacing={1}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}>Set up Google Cloud access</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                Your Google Cloud admin gives one of our service accounts read access to your Carbon Footprint
                export in BigQuery. We never ask for Google Cloud keys or passwords: it can read that one table and
                nothing else, reading it costs you nothing, and you can remove the access at any time.
              </Typography>
            </Stack>
            <LoadingButton variant='contained' loading={isStarting} onClick={startSetup}>
              <Typography>Start setup</Typography>
            </LoadingButton>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant='h6' sx={{ color: 'text.primary' }}>Connect Google Cloud</Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
              Someone who can share BigQuery tables in your Google Cloud project needs to give our service account read
              access to your Carbon Footprint export and label the table with the code below. If that isn&apos;t you,
              email them the instructions.
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 1: give our service account read access</Typography>
            {/* Flex gap, not spacing: wrapped buttons start aligned with a gap above them. */}
            <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
              <Button variant='contained' startIcon={<EmailOutlined fontSize='small' />} onClick={() => openDialog(false)}>
                <Typography sx={{ pl: 0.5 }}>Email instructions</Typography>
              </Button>
              {gcp?.consoleUrl && (
                <Button
                  variant='outlined'
                  component='a'
                  href={gcp.consoleUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  startIcon={<OpenInNewOutlined fontSize='small' />}
                >
                  <Typography sx={{ pl: 0.5 }}>Open Carbon Footprint</Typography>
                </Button>
              )}
              <Button
                variant='text'
                startIcon={<ContentCopyOutlined fontSize='small' />}
                disabled={!gcp?.instructionsText}
                onClick={() => gcp?.instructionsText && copy(gcp.instructionsText, 'Instructions copied. Paste them into your ticket or message.')}
              >
                <Typography sx={{ pl: 0.5 }}>Copy instructions</Typography>
              </Button>
            </Stack>
            {lastSent && (
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                Instructions sent to {lastSent.email} on {moment(lastSent.sentAt).format('D MMM YYYY')}
                {lastSent.sentByName ? ` by ${lastSent.sentByName}` : ''}.{' '}
                <Link component='button' type='button' onClick={() => openDialog(true)} sx={{ verticalAlign: 'baseline' }}>
                  Resend
                </Link>
              </Typography>
            )}
          </Stack>

          <Stack spacing={2}>
            <CopyRow
              label='Service account'
              value={serviceAccount ?? 'Not available'}
              onCopy={serviceAccount ? () => copy(serviceAccount, 'Service account copied to your clipboard') : undefined}
            />
            <CopyRow label='Label key' value={gcp?.labelKey ?? ''} onCopy={() => gcp && copy(gcp.labelKey, 'Label key copied to your clipboard')} />
            <CopyRow label='Label value' value={code} onCopy={() => copy(code, 'Label value copied to your clipboard')} />
            <CodeBlock
              title={gcp?.commands?.usesPlaceholders ? 'Cloud Shell (replace PROJECT and DATASET)' : 'Cloud Shell'}
              text={commandsText}
              onCopy={() => copy(commandsText, 'Commands copied to your clipboard')}
            />
          </Stack>

          <Accordion
            disableGutters
            sx={{ boxShadow: 0, border: '1px solid', borderColor: 'lightGrey.main', borderRadius: '10px', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
              <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Do it in the console instead</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
              <Typography component='ol' variant='body2' sx={{ color: 'text.secondary', pl: 2.5, m: 0, maxWidth: '680px', '& li': { mb: 0.75 } }}>
                <li>
                  If Carbon Footprint is not exported yet: open Carbon Footprint, choose the billing account, then Data export.
                  Choose a project and a dataset, then Save. Schedule a backfill to include months back to January 2021.
                </li>
                <li>In BigQuery, open the carbon_footprint table in that dataset, choose Share, and add the service account above with the role BigQuery Data Viewer.</li>
                <li>Open the table&apos;s Details, choose Edit details, and add a label with the key and value above.</li>
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px', mt: 1.5 }}>
                If Google Cloud refuses the service account with a message about domain restricted sharing, your organisation
                only allows accounts from its own domain. An organisation policy administrator can allow ours for this project
                {gcp?.customerId ? `, by adding our Google Cloud customer ID (${gcp.customerId}) to the allowed list` : ''}.
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Stack spacing={1}>
            <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Step 2: enter the table</Typography>
            <TextField
              size='small'
              fullWidth
              placeholder='my-project.carbon_export.carbon_footprint'
              value={table}
              onChange={e => { setTable(e.target.value); setConnectError(null); }}
              inputProps={{ spellCheck: false, 'aria-label': 'BigQuery table' }}
              helperText='The carbon_footprint table in the dataset chosen for the export. The dataset alone, or a BigQuery console link to the table, works too.'
            />
            {connectError && (
              <Typography variant='body2' sx={{ color: 'error.main' }}>{connectError}</Typography>
            )}
            <Stack direction='row' justifyContent='flex-end'>
              <LoadingButton variant='contained' loading={isConnecting} disabled={!table.trim()} onClick={connect}>
                <Typography>Connect</Typography>
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>

      <SetupInstructionsDialog<ICloudConnectionStatus>
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSent={onUpdated}
        send={recipient => cloudEmissionsService.sendGcpInstructions(recipient)}
        adminLabel='Google Cloud admin'
        description={'They will receive what the access can and cannot do (it can only read your Carbon Footprint export), '
          + 'how to turn the export on, two commands that grant access and add the label, and a request to send the '
          + 'table name back to you. Their reply comes to your email address.'}
        noteMax={AWS_INSTRUCTIONS_NOTE_MAX}
        initialEmail={prefill.email}
        initialName={prefill.name}
      />
    </Card>
  );
};
